import React from 'react';
import { typeFlowerColors, TUTOR_ROOTS_MAX_COUNTS } from '../constants/constants';

// Roots below the student tree, one per tutor talk move type.
//  - Top edge: a slice of the trunk base. Slice widths follow each type's share
//    of all tutor talk moves, so the trunk is always fully covered.
//  - Bottom edge: width = min(count, max) / max of the type's lane, so a root
//    only fills its lane when the count reaches the max for that type.
// Left-to-right order matches the legend: blue, yellow, red.
const ROOT_ORDER = ['accuracy', 'students_relating', 'reasoning'];

const COLORS_BY_FLOWER = {
  blueflower: '#79aaee',
  yellowflower: '#eed571',
  redflower: '#f07d7f',
};

const LABELS = {
  accuracy: 'Accuracy',
  students_relating: 'Students relating',
  reasoning: 'Reasoning',
};

// Geometry is in the student tree's native units (its background is 350px wide
// and the trunk base is 18px wide, centered), so the roots line up with the
// trunk at any scale.
const VIEW_WIDTH = 350;
const VIEW_HEIGHT = 140;
const TRUNK_BASE_WIDTH = 18;
const LANE_WIDTH = VIEW_WIDTH / ROOT_ORDER.length;
const LANE_FILL = 0.9;
const MIN_ROOT_WIDTH = 10;

const getColor = (type) => COLORS_BY_FLOWER[typeFlowerColors[type]] || '#e0e0e0';

// Closed shape: down the left edge, across the bottom, back up the right edge.
// Each edge is a cubic Bezier with vertical tangents at both ends, which gives
// the S-curve from the narrow trunk slice out to the wider bottom.
const buildRootPath = (topLeft, topRight, bottomLeft, bottomRight) => {
  const c1Y = VIEW_HEIGHT * 0.4;
  const c2Y = VIEW_HEIGHT * 0.5;
  return [
    `M ${topLeft} 0`,
    `C ${topLeft} ${c1Y}, ${bottomLeft} ${c2Y}, ${bottomLeft} ${VIEW_HEIGHT}`,
    `L ${bottomRight} ${VIEW_HEIGHT}`,
    `C ${bottomRight} ${c2Y}, ${topRight} ${c1Y}, ${topRight} 0`,
    'Z',
  ].join(' ');
};

export function TutorRoots({ tutorData, scale = 1 }) {
  const counts = tutorData?.processedData || {};
  const total = ROOT_ORDER.reduce((sum, type) => sum + (counts[type] || 0), 0);

  if (total === 0) {
    return (
      <div
        role="status"
        style={{ textAlign: 'center', fontSize: '0.875rem', color: '#6B7280', padding: '12px 0' }}
      >
        No tutor talk
      </div>
    );
  }

  const trunkLeft = (VIEW_WIDTH - TRUNK_BASE_WIDTH) / 2;
  let cursor = trunkLeft;

  const roots = ROOT_ORDER.flatMap((type, laneIndex) => {
    const count = counts[type] || 0;
    if (count === 0) return [];

    const topLeft = cursor;
    cursor += (count / total) * TRUNK_BASE_WIDTH;
    const topRight = cursor;

    const max = TUTOR_ROOTS_MAX_COUNTS[type];
    const fill = Math.min(count, max) / max;
    const width = Math.max(MIN_ROOT_WIDTH, LANE_WIDTH * LANE_FILL * fill);
    const center = LANE_WIDTH * (laneIndex + 0.5);

    return [{
      type,
      count,
      color: getColor(type),
      path: buildRootPath(topLeft, topRight, center - width / 2, center + width / 2),
    }];
  });

  const summary = roots.map(r => `${LABELS[r.type]}: ${r.count}`).join(', ');

  return (
    <svg
      role="img"
      aria-label={`Tutor talk moves visualization. ${summary}`}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      width={VIEW_WIDTH * scale}
      height={VIEW_HEIGHT * scale}
      style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}
    >
      {roots.map(({ type, count, color, path }) => (
        <path key={type} d={path} fill={color}>
          <title>{`${LABELS[type]}: ${count}`}</title>
        </path>
      ))}
    </svg>
  );
}

export default TutorRoots;
